/**
 * Captures console.error calls in a circular buffer for the last 5 minutes.
 * Used to optionally attach error logs to support tickets.
 */

interface ErrorLogEntry {
  timestamp: string;
  args: string[];
}

const MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes
const entries: ErrorLogEntry[] = [];
let originalConsoleError: typeof console.error | null = null;

function pruneOldEntries() {
  const cutoff = Date.now() - MAX_AGE_MS;
  while (entries.length > 0 && new Date(entries[0].timestamp).getTime() < cutoff) {
    entries.shift();
  }
}

export function initErrorLogCollector() {
  if (originalConsoleError) return; // Already initialized

  originalConsoleError = console.error;

  console.error = (...args: unknown[]) => {
    pruneOldEntries();
    entries.push({
      timestamp: new Date().toISOString(),
      args: args.map((a) => {
        try {
          return typeof a === 'string' ? a : JSON.stringify(a);
        } catch {
          return String(a);
        }
      }),
    });
    originalConsoleError!(...args);
  };
}

export function getErrorLogs(): string {
  pruneOldEntries();
  if (entries.length === 0) return '';

  return entries
    .map((e) => `[${e.timestamp}] ${e.args.join(' ')}`)
    .join('\n');
}

export function getErrorLogCount(): number {
  pruneOldEntries();
  return entries.length;
}
